"use client"

import { CalendarClock, Check, CheckCircle2, Cpu, Gauge, Layers, Loader2, Mail, Send, Target, Users, Wallet } from "lucide-react"
import type { DreamProposal } from "@/lib/ai/dream-proposal"
import type { Locale } from "@/lib/i18n"

const copy = {
  tr: {
    kicker: "PROJE VİZYONU · LAKVERSE",
    complexity: "Karmaşıklık", timeline: "Tahmini süre", budget: "Tahmini bütçe", mvp: "İlk sürüm özelliği",
    levels: { low: "Düşük", medium: "Orta", high: "Yüksek", "very-high": "Çok yüksek" } as Record<string, string>,
    why: "Neden kazanır?", audience: "Kimin için?", features: "Özellikler", mvpCol: "İlk sürüm (MVP)", nextCol: "Sonraki aşama",
    roadmap: "Yol haritası", tech: "Önerilen teknolojiler", metrics: "Başarı ölçütleri", story: "Ürün hayata geçtiğinde",
    quote: "Ücretsiz keşif görüşmesi al", quoteSent: "Talebin alındı, seni arayacağız", mail: "Raporu e-postama gönder",
    budgetNote: "Bütçe ve süre, kapsam netleştikçe keşif görüşmesinde kesinleşir (KDV hariç).",
  },
  en: {
    kicker: "PROJECT VISION · LAKVERSE",
    complexity: "Complexity", timeline: "Estimated timeline", budget: "Estimated budget", mvp: "MVP features",
    levels: { low: "Low", medium: "Medium", high: "High", "very-high": "Very high" } as Record<string, string>,
    why: "Why it wins", audience: "Who it's for", features: "Features", mvpCol: "First release (MVP)", nextCol: "Next phase",
    roadmap: "Roadmap", tech: "Recommended technologies", metrics: "Success metrics", story: "Once it's live",
    quote: "Book a free discovery call", quoteSent: "Request received, we'll be in touch", mail: "Email me this report",
    budgetNote: "Budget and timeline are finalised in the discovery call as the scope becomes clear (excl. VAT).",
  },
}

type Props = {
  proposal: DreamProposal
  imageUrl?: string | null
  brandColor?: string
  locale: Locale
  quoteState: "" | "busy" | "sent" | "error"
  onQuote: () => void
  mailState: { busy?: boolean; ok?: boolean }
  onMail: () => void
}

function Heading({ icon: Icon, children }: { icon: typeof Target; children: React.ReactNode }) {
  return (
    <h3 className="mb-4 flex items-center gap-2 font-mono text-xs font-bold tracking-[0.18em] text-slate-500">
      <Icon className="h-4 w-4 text-[#034AA6]" /> {String(children).toUpperCase()}
    </h3>
  )
}

export function ProposalReport({ proposal: p, imageUrl, brandColor = "#034AA6", locale, quoteState, onQuote, mailState, onMail }: Props) {
  const t = copy[locale]
  const mvp = p.features.filter((f) => f.phase === "mvp")
  const later = p.features.filter((f) => f.phase === "next")

  return (
    <article className="mx-auto w-full max-w-[920px] overflow-hidden bg-white shadow-2xl ring-1 ring-slate-200">
      {/* Cover */}
      <header className="relative min-h-[300px] overflow-hidden text-white">
        {imageUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={imageUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${brandColor}, #022859)` }} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/60 to-slate-950/10" />
        <div className="relative flex min-h-[300px] flex-col justify-end p-6 sm:p-10">
          <p className="font-mono text-[11px] font-bold tracking-[0.25em] text-white/70">{t.kicker}</p>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-tight sm:text-4xl">{p.headline}</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/85 sm:text-base">{p.summary}</p>
        </div>
      </header>

      {/* Key numbers */}
      <section className="grid grid-cols-2 border-b border-slate-200 md:grid-cols-4">
        {[
          { icon: Gauge, label: t.complexity, value: t.levels[p.estimatedComplexity] ?? p.estimatedComplexity },
          { icon: CalendarClock, label: t.timeline, value: p.estimatedTimeline },
          { icon: Wallet, label: t.budget, value: p.estimatedBudget },
          { icon: Layers, label: t.mvp, value: String(mvp.length) },
        ].map(({ icon: Icon, label, value }, i) => (
          <div key={label} className={`p-5 ${i % 2 ? "" : "border-r"} border-slate-200 md:border-r ${i === 3 ? "md:border-r-0" : ""} ${i < 2 ? "border-b md:border-b-0" : ""}`}>
            <Icon className="mb-2 h-5 w-5 text-[#034AA6]" />
            <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{label}</p>
            <p className="mt-1 text-lg font-bold text-slate-900">{value || "—"}</p>
          </div>
        ))}
      </section>

      <div className="space-y-10 p-6 sm:p-10">
        {/* Value propositions */}
        {p.valueProps.length > 0 && (
          <section>
            <Heading icon={Target}>{t.why}</Heading>
            <div className="grid gap-4 md:grid-cols-3">
              {p.valueProps.map((v, i) => (
                <div key={v.title} className="relative border border-slate-200 p-5">
                  <span className="absolute -top-3 left-5 bg-[#034AA6] px-2 py-0.5 font-mono text-xs font-bold text-white">0{i + 1}</span>
                  <p className="mt-1 font-semibold text-slate-900">{v.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Audience */}
        {p.audience.length > 0 && (
          <section>
            <Heading icon={Users}>{t.audience}</Heading>
            <div className="grid gap-3 md:grid-cols-3">
              {p.audience.map((a) => (
                <div key={a.segment} className="bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">{a.segment}</p>
                  <p className="mt-1 text-sm text-slate-600">{a.need}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Features */}
        <section>
          <Heading icon={Layers}>{t.features}</Heading>
          <div className="grid gap-6 md:grid-cols-2">
            {[{ title: t.mvpCol, items: mvp, strong: true }, { title: t.nextCol, items: later, strong: false }].filter((c) => c.items.length).map((col) => (
              <div key={col.title}>
                <p className={`mb-3 inline-block px-2 py-1 text-xs font-bold ${col.strong ? "bg-[#034AA6] text-white" : "bg-slate-200 text-slate-700"}`}>{col.title}</p>
                <ul className="space-y-3">
                  {col.items.map((f) => (
                    <li key={f.title} className="flex gap-3">
                      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${col.strong ? "text-[#034AA6]" : "text-slate-400"}`} />
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">{f.title}</span>
                        <span className="block text-sm text-slate-600">{f.description}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Roadmap */}
        {p.phases.length > 0 && (
          <section>
            <Heading icon={CalendarClock}>{t.roadmap}</Heading>
            <ol className="grid gap-4" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(190px, 1fr))` }}>
              {p.phases.map((ph, i) => (
                <li key={ph.name} className="relative border-t-4 border-[#034AA6] bg-slate-50 p-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs font-bold text-[#034AA6]">0{i + 1}</span>
                    <span className="bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 ring-1 ring-slate-200">{ph.duration}</span>
                  </div>
                  <p className="mt-2 font-semibold leading-snug text-slate-900">{ph.name}</p>
                  <ul className="mt-2 space-y-1">
                    {ph.deliverables.map((d) => <li key={d} className="text-xs leading-relaxed text-slate-600">• {d}</li>)}
                  </ul>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Tech + metrics */}
        <section className="grid gap-8 md:grid-cols-2">
          {p.techStack.length > 0 && (
            <div>
              <Heading icon={Cpu}>{t.tech}</Heading>
              <div className="flex flex-wrap gap-2">
                {p.techStack.map((tech) => <span key={tech} className="border border-slate-300 px-3 py-1.5 font-mono text-xs text-slate-700">{tech}</span>)}
              </div>
            </div>
          )}
          {p.metrics.length > 0 && (
            <div>
              <Heading icon={Target}>{t.metrics}</Heading>
              <ul className="space-y-2">
                {p.metrics.map((m) => (
                  <li key={m.label} className="border-l-2 border-[#034AA6] pl-3">
                    <p className="text-sm font-semibold text-slate-900">{m.label}</p>
                    <p className="text-xs text-slate-600">{m.target}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* Story */}
        {p.story && (
          <section>
            <Heading icon={CheckCircle2}>{t.story}</Heading>
            <div className="border-l-4 border-slate-900 pl-5 text-[15px] leading-relaxed text-slate-700">
              {p.story.split(/\n+/).map((para, i) => <p key={i} className={i ? "mt-3" : ""}>{para}</p>)}
            </div>
          </section>
        )}
      </div>

      {/* Closing call to action */}
      <footer className="bg-[#022859] p-6 text-white sm:p-10">
        <p className="max-w-2xl text-lg font-semibold leading-snug sm:text-xl">{p.closing}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button type="button" onClick={onQuote} disabled={quoteState === "busy" || quoteState === "sent"} className="flex h-12 items-center gap-2 bg-white px-6 font-mono text-sm font-bold text-[#022859] transition hover:bg-slate-100 disabled:opacity-80">
            {quoteState === "busy" ? <Loader2 className="h-4 w-4 animate-spin" /> : quoteState === "sent" ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <Send className="h-4 w-4" />}
            {quoteState === "sent" ? t.quoteSent : t.quote}
          </button>
          <button type="button" onClick={onMail} disabled={mailState.busy || mailState.ok} className="flex h-12 items-center gap-2 border border-white/40 px-6 text-sm font-semibold transition hover:bg-white/10 disabled:opacity-70">
            {mailState.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mailState.ok ? <CheckCircle2 className="h-4 w-4 text-green-400" /> : <Mail className="h-4 w-4" />}
            {t.mail}
          </button>
        </div>
        <p className="mt-4 text-xs text-white/60">{t.budgetNote}</p>
      </footer>
    </article>
  )
}
